<template>
  <div class="flex-center position-ref full-height">
    <div class="content">
      <Loading :loading="loading"></Loading>
      <img src="/assets/logos/kidzwonder-3d.png" alt="Kidzwonder 3D" />
      <h3 v-if="!error">{{ status }}</h3>
      <SsoError v-if="error" :message="error"></SsoError>
      <button v-if="showOpenAppButton" type="button" class="open-app-button" @click="goApp(pendingToken)">
        Uygulamayı Aç
      </button>
      <hr v-if="showStoreBadges" class="divider" />
      <p v-if="appNotOpened" class="fallback-message">
        Uygulama otomatik açılmadıysa yukarıdaki "Uygulamayı Aç" düğmesine dokunun. Yine
        açılmazsa cihazınızda Kidzwonder 3D uygulamasının yüklü olduğundan emin olup tekrar deneyin.
      </p>
      <div v-if="showStoreBadges" class="store-badges">
        <a v-if="storeLinks.windows" :href="storeLinks.windows" target="_blank" rel="noopener">
          <img src="/assets/images/microsoft.png" alt="Microsoft Store'dan indirin" />
        </a>
        <a v-if="storeLinks.appStore" :href="storeLinks.appStore" target="_blank" rel="noopener">
          <img src="/assets/images/app-store.png" alt="App Store'dan indirin" />
        </a>
        <a v-if="storeLinks.googlePlay" :href="storeLinks.googlePlay" target="_blank" rel="noopener">
          <img src="/assets/images/google-play.png" alt="Google Play'den indirin" />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import SsoError from "@/components/SsoError";
import axios from "axios";
import CryptoJS from "crypto-js";

/**
 * Kidzwonder 3D de Kidzwonder Jr ile aynı desende: web platformu değil, Unity ile
 * yazılmış native bir app (iOS/Android/Windows). Akış:
 *
 *   Okul platformu → service.ueslms.com/#/kidzwonder-3d?sso=xxx  (bu bileşen)
 *     1. sso AES ile çözülür (okul platformunun ürettiği kullanıcı payload'u)
 *     2. /api/v2/kw3d/user/check → yoksa /api/v2/kw3d/user/create
 *     3. backend'in çözdüğü (kullanıcı payload'ından değil) role_type'a göre:
 *        - student  → dönen JWT, custom URL scheme ile app'e devredilir:
 *                      kidzwonder3d://login?token=<JWT>
 *        - diğerleri (teacher/campusadmin/schooladmin) → aynı JWT ile
 *                      report.kidzwonder3d.com'a yönlendirilir (hash mode: #/sso?token=)
 *
 * Rol için backend'in returnUser() ile döndürdüğü değer kullanılır — okul platformunun
 * gönderdiği role_type değil. Rapor sitesi tarafında da öğrenci JWT'sinin girişi
 * reddedilir (bkz. report-kidzwonder3d/src/views/SsoLoginView.vue), yani bu ayrım bir
 * güvenlik sınırı değil, ikinci bir kontrol katmanına sahip bir yönlendirme.
 *
 * App tarafı bu JWT'yi doğrudan KW3D API'sine (content/homework/reader/report...)
 * Bearer token olarak gönderir. Ayrı bir "app'e özel ikinci şifreleme" katmanı yok,
 * çünkü hedef bizim kendi backend'imiz, üçüncü parti bir platform değil.
 *
 * Bkz. ueslms/.claude/notes/kidzwonder-jr-3d-entegrasyonu.md
 */
export default {
  name: "Kidzwonder3D",
  components: {
    Loading,
    SsoError
  },
  data() {
    return {
      loading: false,
      status: 'Lütfen bekleyiniz, uygulamaya yönlendiriliyorsunuz.',
      error: null,
      appNotOpened: false,
      appNotOpenedTimer: null,
      pendingToken: null,
      secretKey: 'xnD1LuiJpRenBZOCNk_Ziws4wm3Ci3q1E3EzMuxzUOBT-HrA8tL-fOSHaIySKdh8mUMxfMUvd-tQPCrybPwIzg',
      appScheme: 'kidzwonder3d://login',
      reportUrl: 'https://report.kidzwonder3d.com/#/sso',
      userData: null,
      storeLinks: {
        appStore: 'https://apps.apple.com/tr/app/kidzwonder3/id6741059598',
        googlePlay: 'https://play.google.com/store/apps/details?id=com.UES.KidzWonder3d',
        windows: 'https://apps.microsoft.com/detail/9mwdphwf1236',
      },
    }
  },
  beforeMount() {
    const query = this.$route.query;

    if (!query.sso) {
      this.fail('Giriş bağlantısı eksik (sso parametresi yok). Lütfen okul platformu üzerinden tekrar deneyin.');
      return;
    }

    this.loading = true;

    try {
      const decode = decodeURIComponent(atob(query.sso));
      const bytes = CryptoJS.AES.decrypt(decode, this.secretKey).toString(CryptoJS.enc.Utf8);
      this.userData = JSON.parse(bytes);
    } catch (e) {
      this.fail('Giriş bağlantısı çözümlenemedi. Lütfen okul platformu üzerinden tekrar deneyin.');
      return;
    }

    this.ueslmsCheckUser();
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    clearTimeout(this.appNotOpenedTimer);
  },
  computed: {
    // Hem "app açılmadı" fallback'inde hem de hata durumunda (ör. bağlantı
    // çözülemedi) kullanıcı yine de uygulamayı indirebilsin diye gösterilir.
    showStoreBadges() {
      return this.appNotOpened || !!this.error;
    },
    // token elimize geçtiği andan itibaren (otomatik denemeyle aynı anda) gösterilir -
    // bkz. goApp()'teki not: otomatik location.href denemesi tablet/Android'de sessizce
    // engellenebiliyor, bu düğmenin dokunuşu (gerçek user gesture) daha güvenilir.
    showOpenAppButton() {
      return !!this.pendingToken;
    },
  },
  methods: {
    fail(message) {
      this.loading = false;
      this.error = message;
      console.log('Kidzwonder 3D SSO:', message);
    },

    ueslmsCheckUser() {
      axios.post('https://www.ueslms.com/api/v2/kw3d/user/check', {user: this.userData})
      // axios.post('https://ueslms.test/api/v2/kw3d/user/check', {user: this.userData})
          .then(response => {
            if (response.data.success) {
              this.route(response.data.user, response.data.token);
            } else if (response.data.message) {
              // success:false + message → örn. lisans/yetki reddi. Kullanıcı henüz
              // yok diye create'e düşmek yerine reddi doğrudan gösteriyoruz, çünkü
              // create de aynı kontrolden geçip aynı sebeple reddedilecek.
              this.fail(response.data.message);
            } else {
              this.ueslmsCreate();
            }
          })
          .catch(() => {
            this.fail('Sunucuya bağlanılamadı. Lütfen tekrar deneyin.');
          });
    },

    ueslmsCreate() {
      axios.post('https://www.ueslms.com/api/v2/kw3d/user/create', this.userData)
      // axios.post('https://ueslms.test/api/v2/kw3d/user/create', this.userData)
          .then(response => {
            if (response.data.success) {
              this.route(response.data.user, response.data.token);
            } else {
              this.fail(response.data.message || 'Kullanıcı oluşturulamadı. Lütfen tekrar deneyin.');
            }
          })
          .catch(() => {
            this.fail('Sunucuya bağlanılamadı. Lütfen tekrar deneyin.');
          });
    },

    // Rol, backend'in çözdüğü user.role_type'a göre belirlenir (okul platformunun
    // gönderdiği payload'a değil) — bkz. dosya başındaki açıklama.
    route(user, token) {
      if (!token) {
        this.fail('Oturum anahtarı alınamadı. Lütfen tekrar deneyin.');
        return;
      }

      if (user && user.role_type === 'student') {
        this.goApp(token);
      } else {
        this.goReport(token);
      }
    },

    goReport(token) {
      this.loading = false;
      this.status = 'Rapor sitesine yönlendiriliyorsunuz…';
      location.href = this.reportUrl + '?token=' + encodeURIComponent(token);
    },

    /**
     * NOT (06.10.2026, tablet raporu): bu çağrı buraya SebitVCloud.vue'nun window.location.href
     * redirect'i + burada bir axios zincirinin ucunda, yani kullanıcının hiçbir doğrudan
     * tıklamasının olmadığı bir noktada geliyor ("fresh user activation" yok). Android'de Chrome
     * ve çoğu Chromium tabanlı WebView, böyle bir zincirin sonunda çıplak custom scheme'e
     * (kidzwonder3d://...) location.href ile geçişi SESSİZCE engelleyebiliyor - hata vermez,
     * sayfada kalınır, kullanıcı "çalışmıyor" sanır. İki önlem birlikte alındı:
     *   1. Android'de çıplak scheme yerine Intent URL sözdizimi kullanılıyor (package + Play
     *      Store fallback dahil) - bu sözdizimi Chrome'da user-activation kısıtlamasından daha
     *      az etkileniyor ve uygulama yüklü değilse otomatik olarak mağazaya düşüyor.
     *   2. Otomatik deneme başarısız olursa (ya da baştan sessizce engellenirse) kullanıcının
     *      dokunabileceği görünür bir "Uygulamayı Aç" düğmesi var (bkz. showOpenAppButton) -
     *      gerçek bir dokunuş/tıklama her zaman taze user activation taşıdığı için en güvenilir yol.
     * Eğer öğrenci gerçekten Doğa'nın kendi uygulaması içine gömülü bir WebView'da kalıyorsa (tam
     * bağımsız bir tarayıcıya hiç çıkmıyorsa) bu sayfa tarafı düzeltmeler yetmeyebilir - o durumda
     * WebView'ın non-http(s) şemaları nasıl ele aldığı Doğa'nın app tarafında çözülmesi gerekir.
     */
    goApp(token) {
      this.loading = false;
      this.status = 'Uygulamaya yönlendiriliyorsunuz…';
      this.pendingToken = token;

      // Sekme app'e geçince (visibilitychange) fallback mesajı iptal edilir;
      // birkaç saniye sonra hâlâ buradaysak app muhtemelen açılmadı.
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      clearTimeout(this.appNotOpenedTimer);
      this.appNotOpenedTimer = setTimeout(() => {
        this.appNotOpened = true;
      }, 2500);

      const encodedToken = encodeURIComponent(token);

      if (/Android/i.test(navigator.userAgent)) {
        const fallbackUrl = encodeURIComponent(this.storeLinks.googlePlay);
        location.href = `intent://login?token=${encodedToken}#Intent;scheme=kidzwonder3d;package=com.UES.KidzWonder3d;S.browser_fallback_url=${fallbackUrl};end`;
      } else {
        location.href = this.appScheme + '?token=' + encodedToken;
      }
    },

    onVisibilityChange() {
      if (document.hidden) {
        clearTimeout(this.appNotOpenedTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }
    },
  }
}
</script>

<style scoped>
html, body {
  background-color: #712481;
  height: 100vh;
  margin: 0;
}

.flex-center {
  align-items: center;
  display: flex;
  justify-content: center;
}

.position-ref {
  position: relative;
}

.content {
  margin-top: 50px;
  text-align: center;
}

.divider {
  max-width: 420px;
  margin: 24px auto 0;
  border: none;
  border-top: 2px solid #712481;
  opacity: 0.3;
}

.open-app-button {
  margin-top: 20px;
  padding: 12px 28px;
  border: none;
  border-radius: 8px;
  background-color: #712481;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.open-app-button:active {
  opacity: 0.85;
}

.fallback-message {
  max-width: 420px;
  margin: 16px auto 0;
  color: #712481;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
}

.store-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}

.store-badges img {
  height: 44px;
  width: auto;
}
</style>
